import React, { useState } from 'react';
import { CheckCircle2, ArrowRight, Save, Send } from 'lucide-react';

interface FormData {
  purpose: string;
  background: string;
  budget: string;
  timeline: string;
  impact: string;
}

export default function ApplicationBuilder() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    purpose: '',
    background: '',
    budget: '',
    timeline: '',
    impact: ''
  });

  const steps = [
    {
      number: 1,
      title: 'Syfte och Mål',
      description: 'Beskriv projektets huvudsyfte och mål'
    },
    {
      number: 2,
      title: 'Bakgrund',
      description: 'Din bakgrund och kvalifikationer'
    },
    {
      number: 3,
      title: 'Budget',
      description: 'Detaljerad budget och finansieringsplan'
    },
    {
      number: 4,
      title: 'Tidsplan',
      description: 'Projektets tidsram och milstolpar'
    },
    {
      number: 5,
      title: 'Förväntad Effekt',
      description: 'Projektets förväntade resultat och påverkan'
    }
  ];

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (step < steps.length) {
      setStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(prev => prev - 1);
    }
  };

  const handleSubmit = () => {
    // In production, this would send the application
    console.log('Submitting application:', formData);
  };

  const getCurrentField = () => {
    switch (step) {
      case 1:
        return 'purpose';
      case 2:
        return 'background';
      case 3:
        return 'budget';
      case 4:
        return 'timeline';
      case 5:
        return 'impact';
      default:
        return 'purpose';
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-8">
          {steps.map((s, i) => (
            <div key={i} className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  step >= s.number
                    ? 'bg-rose-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step > s.number ? (
                  <CheckCircle2 className="w-6 h-6" />
                ) : (
                  s.number
                )}
              </div>
              <span className="text-sm mt-2 text-gray-600">{s.title}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Current Step */}
      <div className="bg-white rounded-xl p-6 mb-6">
        <h3 className="text-xl font-bold mb-4">{steps[step - 1].title}</h3>
        <p className="text-gray-600 mb-4">{steps[step - 1].description}</p>
        
        <textarea
          value={formData[getCurrentField()]}
          onChange={(e) => handleInputChange(getCurrentField(), e.target.value)}
          className="w-full h-40 p-4 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          placeholder="Skriv här..."
        />
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <button
          onClick={handleBack}
          disabled={step === 1}
          className={`px-6 py-2 rounded-lg ${
            step === 1
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Tillbaka
        </button>

        <div className="flex gap-2">
          <button
            onClick={() => console.log('Saving draft...')}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 
                     flex items-center gap-2"
          >
            <Save className="w-5 h-5" />
            Spara utkast
          </button>

          {step < steps.length ? (
            <button
              onClick={handleNext}
              className="px-6 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 
                       flex items-center gap-2"
            >
              Nästa
              <ArrowRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="px-6 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 
                       flex items-center gap-2"
            >
              Skicka ansökan
              <Send className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}